import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CoffeeIcon, RocketLaunchIcon, WarningIcon, TrendUpIcon, SparkleIcon } from "@phosphor-icons/react";

/**
 * v6.5.56 - Morning Report page.
 * Aggregates last 24h of autopilot activity into a phone-friendly summary.
 * Read this with your coffee, no VPS needed.
 */
export default function MorningReport() {
  const [d, setD] = useState(null);
  const [hours, setHours] = useState(24);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null); setD(null);
    api.get(`/admin/brain/morning-report?hours=${hours}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e?.message || "load failed"));
  }, [hours]);

  const kpiClass = "bg-[#15171E] border border-[#2A2D3A] rounded-md p-4";

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl" data-testid="morning-report-page">
      <header className="flex flex-wrap items-center gap-3">
        <CoffeeIcon size={28} color="#FBBF24" weight="fill" />
        <div>
          <h1 className="text-2xl mono">Morning Report</h1>
          <p className="text-xs text-[#94A3B8] mono">
            What your Brain Autopilot did overnight. Sip and scroll.
          </p>
        </div>
        <div className="flex-1" />
        <select
          data-testid="hours-select"
          value={hours}
          onChange={(e) => setHours(parseInt(e.target.value))}
          className="bg-[#15171E] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-[#F1F5F9]"
        >
          <option value={6}>Last 6h</option>
          <option value={12}>Last 12h</option>
          <option value={24}>Last 24h</option>
          <option value={48}>Last 48h</option>
          <option value={168}>Last 7d</option>
        </select>
      </header>

      {err && <div className="text-[#FF4D4D] mono text-sm">{err}</div>}
      {!d && !err && <div className="text-[#94A3B8] mono text-sm">Loading...</div>}

      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={kpiClass} data-testid="kpi-autopilot-runs">
              <div className="text-[10px] mono uppercase text-[#64748B]">Autopilot Runs</div>
              <div className="mono text-3xl mt-1 text-[#F1F5F9]">{d.autopilot_runs}</div>
            </div>
            <div className={kpiClass} data-testid="kpi-strategies-built">
              <div className="text-[10px] mono uppercase text-[#64748B]">New Strategies</div>
              <div className="mono text-3xl mt-1 text-[#2ECA7F]">{d.strategies_built}</div>
            </div>
            <div className={kpiClass} data-testid="kpi-cycles">
              <div className="text-[10px] mono uppercase text-[#64748B]">Loop Cycles</div>
              <div className="mono text-3xl mt-1 text-[#22D3EE]">
                {d.loop_state?.state?.total_cycles ?? "—"}
              </div>
            </div>
            <div className={kpiClass} data-testid="kpi-build-failures">
              <div className="text-[10px] mono uppercase text-[#64748B]">Build Failures</div>
              <div className="mono text-3xl mt-1" style={{ color: (d.build_failures_sample?.length || 0) === 0 ? "#2ECA7F" : "#FF4D4D" }}>
                {d.build_failures_sample?.length || 0}
              </div>
            </div>
          </div>

          <section>
            <div className="flex items-center gap-2 mb-2">
              <SparkleIcon size={18} color="#8B5CF6" weight="fill" />
              <h2 className="text-lg mono">New Strategies Designed</h2>
            </div>
            {d.new_strategies?.length === 0 ? (
              <div className="text-[#64748B] text-sm mono">None in this window.</div>
            ) : (
              <div className="space-y-2">
                {d.new_strategies?.slice(0, 20).map((s) => (
                  <div key={s.class_name} className="p-3 bg-[#15171E] border border-[#2A2D3A] rounded flex items-center gap-3" data-testid={`new-strat-${s.class_name}`}>
                    <div className="flex-1 min-w-0">
                      <div className="mono text-sm text-[#F1F5F9] truncate">{s.class_name}</div>
                      <div className="mono text-[11px] text-[#94A3B8] truncate">{s.description}</div>
                    </div>
                    <div className="mono text-[10px] text-[#8B5CF6]">{s.family_tag}</div>
                    <div className="mono text-[10px] text-[#22D3EE]">{s.target_symbol || "any"}</div>
                    <span className={`mono text-[10px] px-2 py-0.5 rounded ${
                      s.status === "live" ? "bg-[#2ECA7F]/20 text-[#2ECA7F]" :
                      s.status === "compiled" ? "bg-[#22D3EE]/20 text-[#22D3EE]" :
                      s.status === "failed" ? "bg-[#FF4D4D]/20 text-[#FF4D4D]" :
                      "bg-[#64748B]/20 text-[#94A3B8]"
                    }`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {Object.keys(d.strategies_by_family || {}).length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <TrendUpIcon size={18} color="#2ECA7F" weight="fill" />
                <h2 className="text-lg mono">By Family</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.entries(d.strategies_by_family).map(([fam, n]) => (
                  <div key={fam} className={kpiClass}>
                    <div className="mono text-xs text-[#8B5CF6]">{fam}</div>
                    <div className="mono text-2xl text-[#F1F5F9]">{n}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {d.build_failures_sample?.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-2">
                <WarningIcon size={18} color="#FF4D4D" weight="fill" />
                <h2 className="text-lg mono">Build Failures ({d.build_failures_sample.length})</h2>
              </div>
              <div className="space-y-1">
                {d.build_failures_sample.map((f, i) => (
                  <div key={i} className="mono text-xs text-[#FF4D4D] bg-[#15171E] border border-[#2A2D3A] rounded p-2" data-testid={`build-fail-${i}`}>
                    {f}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-2 mb-2">
              <RocketLaunchIcon size={18} color="#22D3EE" weight="fill" />
              <h2 className="text-lg mono">Autopilot Steps (last {hours}h)</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(d.stage_counts || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => {
                const [step, status] = k.split(":");
                const color = status === "ok" ? "#2ECA7F" : status === "warn" ? "#FBBF24" : "#FF4D4D";
                return (
                  <div key={k} className="p-2 bg-[#15171E] border border-[#2A2D3A] rounded flex items-center gap-2">
                    <div className="mono text-xs text-[#F1F5F9] flex-1">{step}</div>
                    <div className="mono text-xs" style={{ color }}>{status}</div>
                    <div className="mono text-sm text-[#F1F5F9]">{n}</div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
